import styled from 'styled-components';
import { WatsonStatsBlockStyled } from './Common';
import * as React from 'react';
import { EmotionsAndTopicsReport } from '../Logic/EmotionStatsLogic';
import Measure from 'react-measure';
import { analysisStatusDescriptionMessage, Emotion } from '../../../Models';
import appTheme from '../../../Theme';
import { useIntl } from 'react-intl';

interface Ball {
  info: BallInfo;

  x: number;
  y: number;
  radius: number;
  speedX: number;
  speedY: number;
}

enum Style {
  filled,
  bordered,
}

interface BallInfo {
  readonly text: string;
  readonly style: Style;
  readonly backgroundColor: string;
  readonly borderColor: string;
  readonly textColor: string;
  readonly relevance: number;
  readonly mixedEmotionColor?: string;
}

class BallsWorld {
  public summaryRelevance: number;
  public balls: Ball[] = [];
  private height: number;
  private width: number;
  private interval: any = null;
  private speedRatio = 1;

  public start(callback: (balls: ReadonlyArray<Ball>) => void): void {
    this.interval = setInterval(() => {
      callback([...this.run()]);
    }, 1000 / 30);
  }

  public stop(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  public setSize(width: number, height: number): void {
    this.width = width;
    this.height = height;

    this.balls = this.balls.map(value => {
      value.radius = this.getBallRadius(value.info);
      return value;
    });
  }

  public addBall(ballInfo: BallInfo): void {
    let retries = 20;

    GENERATE: while (retries > 0) {
      const newBall = this.makeBall(ballInfo);
      for (const ball of this.balls) {
        if (this.isColliding(newBall, ball)) {
          retries--;
          continue GENERATE;
        }
      }

      this.balls.push(newBall);
      break;
    }

    if (retries === 0) {
      this.balls.push(this.makeBall(ballInfo));
    }
  }

  public clear(): void {
    this.balls.splice(0, this.balls.length);
  }

  public run(): ReadonlyArray<Readonly<Ball>> {
    this.updatePosition();
    this.runWallCollisions();
    this.runBallsCollisions();
    return this.balls;
  }

  private makeBall(info: BallInfo): Ball {
    return {
      info,
      x: Math.random() * 400,
      y: Math.random() * 200,
      radius: this.getBallRadius(info),
      speedX: -1 + Math.random() * 2,
      speedY: -1 + Math.random() * 2,
    };
  }

  private getBallRadius(info: BallInfo): number {
    if (this.width && this.height && this.summaryRelevance) {
      const square = this.width * this.height;

      const nodeSquare = (square * info.relevance) / this.summaryRelevance;
      const nodeSide = Math.max(Math.sqrt(nodeSquare), 100);
      return (nodeSide * 0.7) / 2;
    } else {
      return 10 + Math.random() * 25;
    }
  }

  private updatePosition(): void {
    for (const ball of this.balls) {
      ball.x += ball.speedX * this.speedRatio;
      ball.y += ball.speedY * this.speedRatio;
    }
  }

  private runBallsCollisions(): void {
    for (const ballA of this.balls) {
      for (const ballB of this.balls) {
        if (ballA !== ballB && this.isColliding(ballA, ballB)) {
          this.collideBalls(ballA, ballB);
        }
      }
    }
  }

  private isColliding(ballA: Ball, ballB: Ball): boolean {
    const dx = ballA.x - ballB.x;
    const dy = ballA.y - ballB.y;
    const distance = dx * dx + dy * dy;

    return distance <= (ballA.radius + ballB.radius) * (ballA.radius + ballB.radius);
  }

  private collideBalls(ballA: Ball, ballB: Ball): void {
    const dx = ballA.x - ballB.x;
    const dy = ballA.y - ballB.y;

    const collisionAngle = Math.atan2(dy, dx);

    // Actual speed calculations
    const speedA = Math.sqrt(ballA.speedX * ballA.speedX + ballA.speedY * ballA.speedY);
    const speedB = Math.sqrt(ballB.speedX * ballB.speedX + ballB.speedY * ballB.speedY);

    // Angles/radians calculations
    const directionA = Math.atan2(ballA.speedY, ballA.speedX);
    const directionB = Math.atan2(ballB.speedY, ballB.speedX);

    // "Rotate" angles in preparation for momentum formula
    const rotatedVelocityXA = speedA * Math.cos(directionA - collisionAngle);
    const rotatedVelocityYA = speedA * Math.sin(directionA - collisionAngle);
    const rotatedVelocityXB = speedB * Math.cos(directionB - collisionAngle);
    const rotatedVelocityYB = speedB * Math.sin(directionB - collisionAngle);

    // Conservation of momentum formula (because of rotation, y can be used directly, but x needs some tinkering)
    const finalVelocityXA =
      ((ballA.radius - ballB.radius) * rotatedVelocityXA + (ballB.radius + ballB.radius) * rotatedVelocityXB) /
      (ballA.radius + ballB.radius);
    const finalVelocityXB =
      ((ballA.radius + ballA.radius) * rotatedVelocityXA + (ballB.radius - ballA.radius) * rotatedVelocityXB) /
      (ballA.radius + ballB.radius);

    ballA.speedX =
      Math.cos(collisionAngle) * finalVelocityXA + Math.cos(collisionAngle + Math.PI / 2) * rotatedVelocityYA;
    ballA.speedY =
      Math.sin(collisionAngle) * finalVelocityXA + Math.sin(collisionAngle + Math.PI / 2) * rotatedVelocityYA;

    ballB.speedX =
      Math.cos(collisionAngle) * finalVelocityXB + Math.cos(collisionAngle + Math.PI / 2) * rotatedVelocityYB;
    ballB.speedY =
      Math.sin(collisionAngle) * finalVelocityXB + Math.sin(collisionAngle + Math.PI / 2) * rotatedVelocityYB;

    do {
      ballA.x += ballA.speedX * this.speedRatio;
      ballA.y += ballA.speedY * this.speedRatio;

      ballB.x += ballB.speedX * this.speedRatio;
      ballB.y += ballB.speedY * this.speedRatio;
    } while (this.isColliding(ballA, ballB));
  }

  private runWallCollisions(): void {
    for (const ball of this.balls) {
      if (ball.x - ball.radius < 0) {
        ball.speedX = -ball.speedX;
        ball.x = ball.radius;
      } else if (ball.x + ball.radius >= this.width) {
        ball.speedX = -ball.speedX;
        ball.x = this.width - ball.radius;
      } else if (ball.y + ball.radius >= this.height) {
        ball.speedY = -ball.speedY;
        ball.y = this.height - ball.radius;
      } else if (ball.y - ball.radius < 0) {
        ball.speedY = -ball.speedY;
        ball.y = ball.radius;
      }
    }
  }
}

const EmotionsStatsContainer = styled(WatsonStatsBlockStyled)``;

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  //transform: translate(-50%, -50%);
  position: relative;
  overflow: hidden;
`;

interface BallProps {
  readonly style: any;
  readonly circleInfo: BallInfo;
}

const EmotionBall = (props: BallProps) => {
  return (
    <div style={props.style}>
      <span
        style={{
          margin: '0 auto',
          padding: '10px',
          alignItems: 'center',
          textAlign: 'center',
          fontSize: '12px',
          color: `${props.circleInfo.textColor}`,
        }}
      >
        {props.circleInfo.text}
      </span>
    </div>
  );
};

interface BallsState {
  readonly balls: ReadonlyArray<Readonly<Ball>>;
}

interface BallsComponentProps {
  readonly ballsInfo: ReadonlyArray<BallInfo>;
}

// tslint:disable-next-line:max-classes-per-file
class BallsComponent extends React.PureComponent<BallsComponentProps, BallsState> {
  public state = {
    balls: [] as ReadonlyArray<Ball>,
  };

  public readonly ballsWorld: BallsWorld = new BallsWorld();

  constructor(props: BallsComponentProps, context: any) {
    super(props, context);

    this.addBallsFromProps();
  }

  public componentDidMount(): void {
    this.ballsWorld.start(balls => {
      this.setState({
        balls,
      });
    });
  }

  public componentWillUpdate(
    nextProps: Readonly<BallsComponentProps>,
    nextState: Readonly<BallsState>,
    nextContext: any,
  ): void {
    if (this.props !== nextProps) {
      this.ballsWorld.clear();
    }
  }

  public componentDidUpdate(
    prevProps: Readonly<BallsComponentProps>,
    prevState: Readonly<BallsState>,
    snapshot?: any,
  ): void {
    if (this.props !== prevProps) {
      this.addBallsFromProps();
    }
  }

  public componentWillUnmount(): void {
    this.ballsWorld.stop();
  }

  public render(): React.ReactNode {
    return (
      <Measure bounds={true} onResize={this.updateWorldSize}>
        {({ measureRef }) => (
          <Container ref={measureRef}>
            {this.ballsWorld.balls.map((ball, index) => {
              return (
                <EmotionBall
                  circleInfo={ball.info}
                  key={index}
                  style={{
                    position: 'absolute',
                    borderRadius: '50%',
                    background: ball.info.mixedEmotionColor
                      ? `radial-gradient(circle, ${ball.info.backgroundColor}, ${ball.info.mixedEmotionColor})`
                      : `${ball.info.backgroundColor}`,
                    border: `${ball.info.style === Style.filled ? `none` : `1px solid ${ball.info.borderColor}`}`,
                    transform: 'translate(-50%, -50%)',
                    width: `${ball.radius * 2}px`,
                    height: `${ball.radius * 2}px`,
                    left: `${ball.x}px`,
                    top: `${ball.y}px`,
                    display: `flex`,
                    alignItems: `center`,
                  }}
                />
              );
            })}
          </Container>
        )}
      </Measure>
    );
  }

  private addBallsFromProps(): void {
    this.ballsWorld.summaryRelevance = this.props.ballsInfo.reduce((acc, value) => acc + value.relevance, 0);

    for (const info of this.props.ballsInfo) {
      this.ballsWorld.addBall(info);
    }
  }

  private updateWorldSize = (contentRect: any) => {
    this.ballsWorld.setSize(contentRect.bounds.width, contentRect.bounds.height);
  };
}

const getFilteredBallsInfo = (report: EmotionsAndTopicsReport): ReadonlyArray<BallInfo> => {
  const result: BallInfo[] = [];

  const mainEmotion = report.emotions[0];
  if (mainEmotion) {
    let emotionDescription: string;
    emotionDescription = mainEmotion.subcategory || mainEmotion.category;

    const ballInfo: BallInfo = {
      text: emotionDescription,
      style: Style.filled,
      backgroundColor: getEmotionColor(mainEmotion.category),
      relevance: mainEmotion.relevance,
      borderColor: 'clear',
      textColor: appTheme.color.white,
      mixedEmotionColor: mainEmotion.mixedEmotion ? getEmotionColor(mainEmotion.mixedEmotion) : undefined,
    };
    result.push(ballInfo);
  }

  if (report.topics) {
    for (const concept of report.topics.slice(0, 3)) {
      result.push({
        text: concept.name,
        style: Style.bordered,
        backgroundColor: appTheme.color.white,
        relevance: concept.relevance,
        borderColor: appTheme.color.paleGrey,
        textColor: appTheme.color.black,
      });
    }
  }

  return result;
};

const getEmotionColor = (emotion: Emotion): string => {
  switch (emotion) {
    case Emotion.joy:
      return appTheme.color.butterscotch;
    case Emotion.sadness:
    case Emotion.sandess:
      return appTheme.color.cerulean;
    case Emotion.disgust:
      return appTheme.color.battleshipGrey;
    case Emotion.fear:
      return appTheme.color.warmBlue;
    case Emotion.anger:
      return appTheme.color.coral;
    case Emotion.excitement:
      return appTheme.color.lavender;
    case Emotion.love:
      return appTheme.color.romanticRose;
    case Emotion.confusion:
      return appTheme.color.teaGreen;
    case Emotion.surprise:
      return appTheme.color.burntSienna;
    case Emotion.shame:
      return appTheme.color.shameEmotion;
  }
};

const EmptyDataComponent = (props: EmotionsAndTopicsReport) => {
  let descriptionString: string;
  const intl = useIntl();

  const failedStatus = props.failedStatuses && props.failedStatuses[0];
  if (failedStatus) {
    descriptionString = analysisStatusDescriptionMessage(failedStatus);
  } else {
    descriptionString = intl.formatMessage({
      id: `containers.Insights.EmotionsAndTopics.status.empty`,
    });
  }

  return <InfoText>{descriptionString}</InfoText>;
};

const InfoText = styled.span`
  font-size: 14px;
  color: ${p => p.theme.color.battleshipGrey};
  margin-bottom: 23px;
`;

export const EmotionsAndTopicsStats = (props: EmotionsAndTopicsReport) => {
  const intl = useIntl();
  return (
    <EmotionsStatsContainer
      title={intl.formatMessage({
        id: `containers.Insights.EmotionsAndTopics.title`,
      })}
      className={props.className}
    >
      {props.topics.length === 0 && props.topics.length === 0 ? (
        <EmptyDataComponent emotions={props.emotions} topics={props.topics} failedStatuses={props.failedStatuses} />
      ) : (
        <BallsComponent ballsInfo={getFilteredBallsInfo(props)} />
      )}
    </EmotionsStatsContainer>
  );
};



// WEBPACK FOOTER //
// ./src/containers/Stats/Components/EmotionsAndTopicsStats.tsx